/**
 * ChatView — active conversation stream.
 * - Other messages: plain prose with optional inline tool-call card.
 * - Self messages: soft surface card right-aligned with timestamp.
 * - Composer: 2-line tall card, attach bottom-left, accent FAB send bottom-right.
 * - Latest agent message types out char-by-char (typewriter effect).
 */

import { Icon } from "./Icon";
import { Avatar } from "./Avatar";
import { renderMarkdown } from "../lib/markdown";
import { startTypewriter } from "../lib/typewriter";
import { atmosWS } from "../lib/atmosWS";
import { atmosStore } from "../lib/atmosStore";
import { appendWithDivider, resetDividers } from "../lib/streamRender";
import { loadDraft, saveDraft, clearDraft, debounce } from "../lib/draft";
import { ScrollToBottomFAB } from "./ScrollToBottomFAB";
import { MsgSkeleton } from "./MsgSkeleton";

// 2026-05-21 P1: mock-messages dropped. Live conv shows ONLY frames that
// arrive after this view mounts (msg.delta → renderLive). History
// backfill is a P-next item (msg.history.q/r) — see SESSION_HANDOFF.md.
interface Message {
  id: string;
  from: string;
  text: string;
  ts: string;
  self: boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function findTarget(
  id: string,
): { name: string; iconKey?: string; sub: string } | null {
  if (id === "new") {
    return { name: "New chat", iconKey: undefined, sub: "draft" };
  }
  if (id === "") {
    return { name: "Atmosphere", iconKey: undefined, sub: "no chats yet" };
  }
  // Try live conv first.
  const cidNum = Number(id);
  if (Number.isFinite(cidNum)) {
    const conv = atmosStore.convs().find((c) => c.cid === cidNum);
    if (conv) return { name: conv.name || "Chat", sub: conv.state };
  }
  // Else try live agent.
  const agent = atmosStore.agents().find((a) => a.id === id);
  if (agent) {
    return { name: agent.name, iconKey: agent.iconKey, sub: agent.status };
  }
  return { name: id, sub: "" };
}

function formatMsgTime(tsSec: string): string {
  const ts = Number.parseInt(tsSec, 10);
  if (!Number.isFinite(ts)) return "";
  const d = new Date(ts * 1000);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, "0")} ${ampm}`;
}

function buildMessage(
  msg: Message,
  opts: { typewrite: boolean },
): HTMLElement {
  const wrap = el(
    "div",
    `chat-view__msg ${msg.self ? "chat-view__msg--self" : "chat-view__msg--other"}`,
  );

  if (!msg.self) {
    const head = el("div", "chat-view__msg-head");
    head.appendChild(el("span", "chat-view__msg-author", msg.from));
    head.appendChild(el("span", "chat-view__msg-time", formatMsgTime(msg.ts)));
    wrap.appendChild(head);
  }

  const text = el("div", "chat-view__msg-text");
  if (msg.self) {
    // Self = plain text in card (no markdown to avoid layout surprises).
    text.textContent = msg.text;
  } else if (opts.typewrite) {
    // Live agent response — stream char-by-char then markdown-render at end.
    startTypewriter(text, msg.text);
  } else {
    text.innerHTML = renderMarkdown(msg.text);
  }
  wrap.appendChild(text);

  if (msg.self) {
    wrap.appendChild(
      el("div", "chat-view__msg-time chat-view__msg-time--self", formatMsgTime(msg.ts)),
    );
  }
  return wrap;
}

function buildComposer(
  activeId: string,
  onSend: (text: string) => void,
  onAttach: (file: File) => void,
  onSlash: (q: string) => void,
): HTMLElement {
  const composer = el("footer", "composer");

  const fieldWrap = el("div", "composer__field-wrap");
  const field = document.createElement("textarea");
  field.className = "composer__field";
  field.rows = 1;
  field.placeholder = "Message Atmosphere…";
  field.setAttribute("aria-label", "Message composer");
  fieldWrap.appendChild(field);
  composer.appendChild(fieldWrap);

  // Restore saved draft for this chat. Debounced save fires on every
  // keystroke; clearDraft on send (in submit()) drops the key entirely.
  const saved = loadDraft(activeId);
  if (saved) {
    field.value = saved;
    // Defer the auto-grow + send-enable to after mount so scrollHeight
    // is measurable.
    requestAnimationFrame(() => {
      field.style.height = "auto";
      field.style.height = `${Math.min(field.scrollHeight, 168)}px`;
    });
  }
  const persistDraft = debounce((v: string) => saveDraft(activeId, v), 250);

  const actions = el("div", "composer__actions");

  // Hidden file input — clicked by the attach button.
  const fileInput = document.createElement("input");
  fileInput.type = "file";
  fileInput.className = "composer__file-input";
  fileInput.setAttribute("aria-hidden", "true");
  fileInput.addEventListener("change", () => {
    const f = fileInput.files?.[0];
    if (f) onAttach(f);
    fileInput.value = "";
  });
  composer.appendChild(fileInput);

  const attach = el("button", "composer__icon-btn");
  attach.type = "button";
  attach.setAttribute("aria-label", "Attach file");
  attach.appendChild(Icon("attach", 18));
  attach.addEventListener("click", () => fileInput.click());
  actions.appendChild(attach);

  // Slash command palette
  const slashBtn = el("button", "composer__icon-btn");
  slashBtn.type = "button";
  slashBtn.setAttribute("aria-label", "Commands");
  slashBtn.appendChild(document.createTextNode("/"));
  slashBtn.style.fontFamily = "var(--font-mono)";
  slashBtn.style.fontWeight = "600";
  slashBtn.addEventListener("click", () => onSlash(field.value));
  actions.appendChild(slashBtn);

  const mic = el("button", "composer__icon-btn");
  mic.type = "button";
  mic.setAttribute("aria-label", "Record voice");
  mic.appendChild(Icon("mic", 18));
  actions.appendChild(mic);

  const spacer = el("div", "composer__spacer");
  actions.appendChild(spacer);

  const send = el("button", "composer__send");
  send.type = "button";
  send.setAttribute("aria-label", "Send message");
  send.disabled = true;
  send.appendChild(Icon("send", 18));
  actions.appendChild(send);

  function submit(): void {
    const text = field.value.trim();
    if (!text) return;
    onSend(text);
    field.value = "";
    field.style.height = "auto";
    send.disabled = true;
    clearDraft(activeId);
    persistDraft.flush();
    field.focus();
  }

  send.addEventListener("click", submit);

  field.addEventListener("input", () => {
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, 168)}px`;
    send.disabled = field.value.trim().length === 0;
    // Auto-open palette when user types "/" at the very start.
    if (field.value === "/") {
      onSlash("/");
      field.value = "";
      send.disabled = true;
    }
    persistDraft.call(field.value);
  });

  field.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  });

  composer.appendChild(actions);
  // Expose a method for outer callers to prefill the textarea (used by
  // the command palette to drop in a `/command ` template).
  (composer as unknown as { __setFieldValue?: (v: string) => void }).__setFieldValue =
    (v: string): void => {
      field.value = v;
      field.dispatchEvent(new Event("input"));
      field.focus();
    };
  return composer;
}

export function ChatView(
  activeId: string,
  onMenuOpen: () => void,
  onActivityOpen: () => void,
  onSettingsOpen: () => void,
  onCmdOpen?: (prefill: (text: string) => void) => void,
): HTMLElement {
  const root = el("section", "chat-view");
  root.setAttribute("aria-label", "Conversation");
  const target = findTarget(activeId);

  // Header — TG shape: round ghost back | centered title | tappable avatar.
  // No bar background. Buttons float over the stream.
  const header = el("header", "chat-view__header");

  const backBtn = el("button", "chat-view__hdr-btn");
  backBtn.type = "button";
  backBtn.setAttribute("aria-label", "Open chats");
  backBtn.appendChild(Icon("menu", 18));
  backBtn.addEventListener("click", onMenuOpen);
  header.appendChild(backBtn);

  const title = el("button", "chat-view__title");
  title.type = "button";
  title.setAttribute("aria-label", "Open activity");
  title.appendChild(el("div", "chat-view__title-name", target?.name ?? "No chat"));
  const subRow = el("div", "chat-view__title-sub");
  const link = el("span", `chat-view__link chat-view__link--${atmosStore.linkState()}`);
  link.setAttribute("aria-label", `Link ${atmosStore.linkState()}`);
  subRow.appendChild(link);
  subRow.appendChild(document.createTextNode(target?.sub ?? ""));
  title.appendChild(subRow);
  title.addEventListener("click", onActivityOpen);
  header.appendChild(title);

  // Keep the link dot fresh while this view lives.
  const unsubLink = atmosStore.onLink((state) => {
    link.className = `chat-view__link chat-view__link--${state}`;
    link.setAttribute("aria-label", `Link ${state}`);
  });
  // Clean up on detach so we don't leak subs across rerenders.
  ((root as unknown) as { __cancel?: Array<() => void> }).__cancel ??= [];
  ((root as unknown) as { __cancel: Array<() => void> }).__cancel.push(unsubLink);

  const avatarBtn = el("button", "chat-view__hdr-avatar");
  avatarBtn.type = "button";
  avatarBtn.setAttribute("aria-label", "Open settings");
  avatarBtn.appendChild(Avatar({ name: "Tariqul Islam", sizePx: 36 }));
  avatarBtn.addEventListener("click", onSettingsOpen);
  header.appendChild(avatarBtn);

  root.appendChild(header);

  // Stream
  const stream = el("div", "chat-view__stream");
  stream.setAttribute("role", "log");

  // Loading skeleton — shown for the first ~150ms after each chat
  // switch so the new ChatView never paints empty before its messages
  // populate. Removed before the real bubbles are appended.
  const skeleton = MsgSkeleton({ rows: 3 });
  stream.appendChild(skeleton);

  resetDividers(stream);
  const msgs: Message[] = [];

  // cid + validity must be resolved BEFORE any history/live wiring below
  // references them (TDZ trap — earlier code hit ReferenceError when
  // these were declared further down).
  const isNew = activeId === "new";
  const liveCid = Number(activeId);
  const liveValid = !isNew && Number.isFinite(liveCid);

  // Request history from Mac once paired (if cid is valid).
  if (liveValid && atmosWS.linkState() === "paired") {
    atmosWS.requestHistory(liveCid);
  }

  // History callback: convert wire entries → Message[] and merge before paintInitial.
  if (liveValid) {
    atmosStore.onHistory(liveCid, () => {
      const hist = atmosStore.history(liveCid);
      msgs.length = 0;
      for (const entry of hist) {
        msgs.push({
          id: `h-${entry.ts}`,
          from: entry.role === "user" ? "You" : (target?.name ?? "Agent"),
          text: entry.text,
          ts: String(entry.ts),
          self: entry.role === "user",
        });
      }
    });
  }

  function paintInitial(): void {
    skeleton.remove();
    if (msgs.length === 0) {
      const placeholder = activeId === "new"
        ? "New chat — say hi and Mac will spawn an agent."
        : activeId === ""
          ? "No chat selected. Open the drawer to pick one or tap + to start a new one."
          : "Say hi — no messages yet.";
      stream.appendChild(el("div", "chat-view__empty", placeholder));
      return;
    }
    const lastIdx = msgs.length - 1;
    for (let i = 0; i < msgs.length; i++) {
      const m = msgs[i]!;
      const typewrite = i === lastIdx && !m.self;
      appendWithDivider(stream, m.ts, buildMessage(m, { typewrite }));
    }
  }
  setTimeout(paintInitial, 150);

  root.appendChild(stream);

  // Scroll-to-bottom FAB — flashes in when a delta arrives while the
  // user is scrolled up. Tap = smooth-scroll to latest.
  const fab = ScrollToBottomFAB({ stream });
  root.appendChild(fab.el);
  ((root as unknown) as { __cancel: Array<() => void> }).__cancel.push(fab.dispose);

  // ── Local-only send: append a self bubble + a canned agent echo ──
  let nextMsgId = msgs.length + 1;

  function pushMessage(msg: Message, typewrite: boolean): void {
    // Drop empty-state placeholder once we start adding messages.
    const empty = stream.querySelector(".chat-view__empty");
    if (empty) empty.remove();
    appendWithDivider(stream, msg.ts, buildMessage(msg, { typewrite }));
    requestAnimationFrame(() => {
      stream.scrollTop = stream.scrollHeight;
      fab.sync();
    });
  }

  // Live agent bubble — created lazily on first delta or first tool
  // step, re-rendered each tick. The chip strip is mounted ABOVE the
  // text element so tool calls surface even before the model emits
  // any prose (e.g. a Bash-first turn).
  //
  // Perf: during streaming we APPEND the new delta as plain text
  // (textContent +=) instead of re-rendering the whole buffer via
  // innerHTML on every frame. The full markdown pass happens ONCE on
  // msg.end. This eliminates O(N²) layout thrash on long replies.
  let liveWrap: HTMLElement | null = null;
  let liveStepsEl: HTMLElement | null = null;
  let liveTextEl: HTMLElement | null = null;
  let liveStreamedLen = 0;
  let scrollPendingRAF: number | null = null;

  function ensureLiveBubble(): void {
    if (liveWrap) return;
    const empty = stream.querySelector(".chat-view__empty");
    if (empty) empty.remove();
    liveWrap = el("div", "chat-view__msg chat-view__msg--other");
    const head = el("div", "chat-view__msg-head");
    head.appendChild(el("span", "chat-view__msg-author", target?.name ?? "Agent"));
    head.appendChild(el("div", "chat-view__msg-time", formatMsgTime(String(Math.floor(Date.now() / 1000)))));
    liveWrap.appendChild(head);
    liveStepsEl = el("div", "chat-view__steps");
    liveWrap.appendChild(liveStepsEl);
    liveTextEl = el("div", "chat-view__msg-text chat-view__msg-text--streaming");
    liveWrap.appendChild(liveTextEl);
    liveStreamedLen = 0;
    appendWithDivider(stream, String(Math.floor(Date.now() / 1000)), liveWrap);
  }

  function maybeScroll(): void {
    if (scrollPendingRAF !== null) return;
    const nearBottom =
      stream.scrollHeight - stream.clientHeight - stream.scrollTop <= 80;
    if (!nearBottom) { fab.showIfAway(); return; }
    scrollPendingRAF = requestAnimationFrame(() => {
      scrollPendingRAF = null;
      stream.scrollTop = stream.scrollHeight;
    });
  }

  function renderSteps(): void {
    if (!liveValid) return;
    const steps = atmosStore.steps(liveCid);
    if (steps.length === 0) return;
    ensureLiveBubble();
    if (!liveStepsEl) return;
    // Append-only: only chips we haven't rendered yet. Last chip
    // pulses while still active; flips to --done on freeze.
    const already = liveStepsEl.children.length;
    for (let i = already; i < steps.length; i += 1) {
      const s = steps[i]!;
      const chip = el("span", `chat-view__step chat-view__step--${s.kind}`);
      const name = el("span", "chat-view__step-name", s.text);
      chip.appendChild(name);
      if (s.detail) {
        chip.appendChild(el("span", "chat-view__step-sep", "·"));
        chip.appendChild(el("span", "chat-view__step-detail", s.detail));
      }
      liveStepsEl.appendChild(chip);
    }
    const kids = liveStepsEl.children;
    for (let i = Math.max(0, kids.length - 2); i < kids.length; i += 1) {
      const st = steps[i];
      if (!st) continue;
      kids[i]!.classList.toggle("chat-view__step--active", !st.done);
      kids[i]!.classList.toggle("chat-view__step--done", st.done);
    }
  }

  function renderLiveDelta(): void {
    const buf = liveValid ? atmosStore.messages(liveCid) : "";
    if (!buf) return;
    ensureLiveBubble();
    if (!liveTextEl) return;
    if (buf.length > liveStreamedLen) {
      const delta = buf.slice(liveStreamedLen);
      liveStreamedLen = buf.length;
      // textContent += is a single layout, no parser invocation,
      // no innerHTML wipe-and-rebuild. Markdown rendering deferred
      // until msg.end via renderLiveEnd.
      liveTextEl.appendChild(document.createTextNode(delta));
    }
    maybeScroll();
  }

  function renderLiveEnd(): void {
    if (!liveTextEl) return;
    const full = liveValid ? atmosStore.messages(liveCid) : "";
    // One-shot markdown swap. textContent stays in place if buf is
    // empty (rare — only if msg.end arrived before any delta).
    liveTextEl.classList.remove("chat-view__msg-text--streaming");
    if (full) liveTextEl.innerHTML = renderMarkdown(full);
    maybeScroll();
  }

  if (liveValid) {
    atmosStore.onMessages(liveCid, renderLiveDelta);
    atmosStore.onMessageEnd(liveCid, renderLiveEnd);
    atmosStore.onSteps(liveCid, renderSteps);
    renderLiveDelta();
    renderSteps();
  }

  root.appendChild(
    buildComposer(
      activeId,
      (text) => {
        const nowSec = String(Math.floor(Date.now() / 1000));
        pushMessage(
          { id: `m${nextMsgId++}`, from: "You", text, ts: nowSec, self: true },
          false,
        );
        liveTextEl = null; liveStepsEl = null; liveWrap = null;
        if (liveValid) atmosStore.clearTurn(liveCid);
        const delivered = isNew
          ? atmosWS.sendNew(text) // Mac will spawn + push conv.update; App swaps activeId.
          : liveValid
            ? atmosWS.sendMsg(liveCid, text)
            : false;
        if (!delivered) {
          // Mac link is down or half-open. Don't let the user keep
          // typing into a void — show a one-line system bubble naming
          // the actual link state so they know to re-pair.
          const state = atmosStore.linkState();
          pushMessage(
            {
              id: `m${nextMsgId++}`,
              from: target?.name ?? "system",
              text: `Not delivered — link is ${state}. Settings → Mac sync to re-pair.`,
              ts: String(Math.floor(Date.now() / 1000)),
              self: false,
            },
            false,
          );
        }
      },
      (file) => {
        // Optimistic self bubble describing the upload.
        const nowSec = String(Math.floor(Date.now() / 1000));
        const sizeKB = Math.max(1, Math.round(file.size / 1024));
        pushMessage(
          {
            id: `m${nextMsgId++}`,
            from: "You",
            text: `📎 ${file.name} · ${sizeKB} KB`,
            ts: nowSec,
            self: true,
          },
          false,
        );
        liveTextEl = null; liveStepsEl = null; liveWrap = null;
        if (liveValid && atmosStore.linkState() === "paired") {
          atmosWS.sendAttach(liveCid, file).catch((err) => {
            pushMessage(
              {
                id: `m${nextMsgId++}`,
                from: target?.name ?? "system",
                text: `Attach failed: ${String((err as Error).message ?? err)}`,
                ts: String(Math.floor(Date.now() / 1000)),
                self: false,
              },
              false,
            );
          });
        } else {
          // No Mac paired yet — explain in the stream instead of silently dropping.
          pushMessage(
            {
              id: `m${nextMsgId++}`,
              from: target?.name ?? "system",
              text: "Not paired with Mac yet — open Settings → Mac sync → scan QR to enable file delivery.",
              ts: String(Math.floor(Date.now() / 1000)),
              self: false,
            },
            false,
          );
        }
      },
      (q) => onSlashOpen(q),
    ),
  );

  // Composer is appended last in DOM; grab a typed handle for the
  // command palette's "prefill the field" callback.
  const composerEl = root.querySelector(".composer") as HTMLElement | null;
  function prefillComposer(text: string): void {
    (composerEl as unknown as { __setFieldValue?: (v: string) => void })
      ?.__setFieldValue?.(text);
  }

  // onSlashOpen handler — closure for the composer wiring above.
  function onSlashOpen(q: string): void {
    void q; void prefillComposer; // referenced by external command palette
    onCmdOpen?.(prefillComposer);
  }

  requestAnimationFrame(() => {
    stream.scrollTop = stream.scrollHeight;
  });

  return root;
}

export default ChatView;
